// A/B readout for form variants: which one converts best, and whether the
// difference is real yet. Pure (no React) so it's unit-testable.
//
// Views and conversions both count once per browser (see the backend's
// AccessWebForm / countConversion), so conversions / views is a per-visitor
// conversion rate and a two-proportion z-test applies.

// Below these, any "winner" is mostly noise, so the readout says to wait.
export const MIN_VIEWS_PER_VARIANT = 100;
export const MIN_CONVERSIONS_TOTAL = 10;
export const CONFIDENCE = 0.95;

// Standard normal CDF (Abramowitz & Stegun 7.1.26; error < 1.5e-7).
export function normalCdf(z) {
  const t = 1 / (1 + 0.3275911 * Math.abs(z) / Math.SQRT2);
  const y = 1 - (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-(z * z) / 2);
  return z >= 0 ? (1 + y) / 2 : (1 - y) / 2;
}

const Z_ALPHA = 1.959964; // two-sided 95%
const Z_BETA = 0.841621; // 80% power

// Wilson score interval for a rate — sane at small counts and 0%/100%.
export function wilson(conversions, views, z = Z_ALPHA) {
  if (!views) return { low: 0, high: 0 };
  const p = conversions / views;
  const d = 1 + (z * z) / views;
  const center = (p + (z * z) / (2 * views)) / d;
  const half = (z * Math.sqrt((p * (1 - p)) / views + (z * z) / (4 * views * views))) / d;
  return { low: Math.max(0, center - half), high: Math.min(1, center + half) };
}

// Two-sided p-value that two conversion rates differ (pooled z-test).
export function twoProportionP(c1, n1, c2, n2) {
  if (!n1 || !n2) return 1;
  const pooled = (c1 + c2) / (n1 + n2);
  const se = Math.sqrt(pooled * (1 - pooled) * (1 / n1 + 1 / n2));
  if (!se) return 1;
  const z = (c1 / n1 - c2 / n2) / se;
  return 2 * (1 - normalCdf(Math.abs(z)));
}

// Views per variant needed to detect the gap between two rates at 95%/80%.
export function viewsNeeded(p1, p2) {
  const diff = Math.abs(p1 - p2);
  if (!diff) return Infinity;
  return Math.ceil(((Z_ALPHA + Z_BETA) ** 2 * (p1 * (1 - p1) + p2 * (1 - p2))) / (diff * diff));
}

// analyze returns per-variant rows plus a verdict:
//   { status: "single" }        fewer than two active variants — nothing to compare
//   { status: "collecting" }    not enough data to call it yet
//   { status: "winner", winner, lift, pValue }
//   { status: "no_difference", leader, moreViews }  enough data, no clear winner yet
// Only active variants compete; paused ones are listed with their numbers.
export function analyze(variants) {
  const rows = (variants || []).map((v) => {
    const views = v.views || 0;
    const conversions = v.conversions || 0;
    return {
      id: v.id,
      name: v.name || "Untitled form",
      active: !!v.should_run,
      views,
      conversions,
      rate: views ? conversions / views : 0,
      ci: wilson(conversions, views),
    };
  });

  const active = rows.filter((r) => r.active);
  if (active.length < 2) return { rows, status: "single" };

  const ranked = [...active].sort((a, b) => b.rate - a.rate);
  const [leader, runnerUp] = ranked;
  const totalConversions = active.reduce((s, r) => s + r.conversions, 0);
  const minViews = Math.min(...active.map((r) => r.views));

  if (minViews < MIN_VIEWS_PER_VARIANT || totalConversions < MIN_CONVERSIONS_TOTAL) {
    return {
      rows,
      status: "collecting",
      leader,
      viewsToGo: Math.max(0, MIN_VIEWS_PER_VARIANT - minViews),
      conversionsToGo: Math.max(0, MIN_CONVERSIONS_TOTAL - totalConversions),
    };
  }

  // The leader has to beat the runner-up; the rest trail further. (With many
  // variants this is a little optimistic — fine for a readout, not a paper.)
  const pValue = twoProportionP(leader.conversions, leader.views, runnerUp.conversions, runnerUp.views);
  if (pValue < 1 - CONFIDENCE) {
    return {
      rows,
      status: "winner",
      winner: leader,
      runnerUp,
      lift: runnerUp.rate ? leader.rate / runnerUp.rate - 1 : null,
      pValue,
    };
  }

  const needed = viewsNeeded(leader.rate, runnerUp.rate);
  return {
    rows,
    status: "no_difference",
    leader,
    runnerUp,
    pValue,
    moreViews: Number.isFinite(needed) ? Math.max(0, needed - minViews) : null,
  };
}
